import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { RepeatIcon, CpuIcon, EyeIcon, BrainIcon, SparklesIcon, BookOpenIcon, MessageSquareOffIcon, CompassIcon, LayoutGridIcon } from 'lucide-react';
const GOLD = '#d8b878';
const ITEMS = [{
  icon: RepeatIcon,
  text: 'Sientes que repites patrones que ya no quieres seguir viviendo.'
}, {
  icon: CpuIcon,
  text: 'Has sentido que tu vida funciona en piloto automático.'
}, {
  icon: EyeIcon,
  text: 'Te cuestionas la realidad que te enseñaron como “normal”.'
}, {
  icon: BrainIcon,
  text: 'Sientes que muchas de tus creencias no nacieron realmente de ti.'
}, {
  icon: SparklesIcon,
  text: 'Te interesa el crecimiento personal, la espiritualidad y la expansión de conciencia.'
}, {
  icon: BookOpenIcon,
  text: 'Buscas una lectura profunda, diferente y confrontadora.'
}, {
  icon: MessageSquareOffIcon,
  text: 'Estás cansado de frases bonitas que no te llevan a mirar más profundo.'
}, {
  icon: CompassIcon,
  text: 'Quieres comprender mejor tu mundo interior y recuperar claridad.'
}, {
  icon: LayoutGridIcon,
  text: 'Sientes que llegó el momento de observar el tablero completo.'
}];
export function Problem() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 24
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <section className="relative overflow-hidden bg-matrix-dark py-20 md:py-28 px-5 md:px-8" data-id="element-280">
      {/* Subtle digital texture + depth */}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-[0.04]" style={{
      backgroundImage: 'repeating-linear-gradient(90deg, rgba(0,255,65,0.6) 0px, rgba(0,255,65,0.6) 1px, transparent 1px, transparent 10px)',
      maskImage: 'radial-gradient(ellipse at center, black 25%, transparent 78%)',
      WebkitMaskImage: 'radial-gradient(ellipse at center, black 25%, transparent 78%)'
    }} data-id="element-281"></div>
      <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top,_rgba(216,184,120,0.06)_0%,_transparent_55%)]" data-id="element-282"></div>
      {/* Ambient depth: soft green light rising from the bottom (not a flat curtain) */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-2/3 bg-[radial-gradient(ellipse_at_bottom,_rgba(0,255,65,0.08)_0%,_transparent_62%)]" data-id="element-283"></div>
      {/* Smooth transition from the darker section above */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-24 bg-gradient-to-b from-matrix-black to-transparent" data-id="element-284"></div>

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-285">
        {/* Title */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.8
      }} className="text-center mb-12 md:mb-16" data-id="element-286">
          <span className="inline-block font-inter text-[10px] md:text-xs tracking-[0.4em] uppercase mb-5" style={{
          color: 'rgba(216,184,120,0.7)'
        }} data-id="element-287">
            El espejo
          </span>
          <h2 className="font-cinzel text-3xl md:text-5xl text-matrix-cream drop-shadow-[0_0_14px_rgba(245,230,200,0.15)]" data-id="element-288">
            Este ebook es para ti si….
          </h2>
          <div className="w-16 h-[2px] bg-matrix-green/50 mx-auto mt-6 rounded-full" data-id="element-289"></div>
        </motion.div>

        {/* Cards grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-290">
          {ITEMS.map(({
          icon: Icon,
          text
        }, i) => <motion.div key={i} variants={itemVariants} whileHover={{
          y: -4,
          boxShadow: '0 0 32px -8px rgba(216,184,120,0.35)'
        }} className="group flex items-start gap-4 rounded-xl border border-matrix-green/15 bg-matrix-black/60 backdrop-blur-sm p-5 md:p-6 transition-colors duration-300 hover:border-[rgba(216,184,120,0.4)]" data-id="element-291">
              <span className="shrink-0 flex h-11 w-11 items-center justify-center rounded-lg border border-[rgba(216,184,120,0.2)] bg-matrix-green/[0.06] transition-colors group-hover:border-[rgba(216,184,120,0.45)]" style={{
            color: GOLD
          }} data-id="element-292">
                <Icon size={20} strokeWidth={1.5} data-id="element-293" />
              </span>
              <p className="text-matrix-white/85 text-[14.5px] md:text-base font-light leading-relaxed pt-1.5" data-id="element-294">
                {text}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Closing phrase — emotional conclusion */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.9,
        delay: 0.2
      }} className="mt-14 md:mt-20 mx-auto max-w-3xl text-center" data-id="element-295">
          <div className="w-px h-10 bg-gradient-to-b from-transparent via-[rgba(216,184,120,0.5)] to-transparent mx-auto mb-8" data-id="element-296"></div>
          <p className="font-cinzel text-xl sm:text-2xl md:text-3xl leading-[1.5] text-matrix-cream" style={{
          textShadow: '0 0 26px rgba(216,184,120,0.2)'
        }} data-id="element-297">
            Tal vez no necesitas hacer más de lo mismo. Tal vez necesitas{' '}
            <span className="italic" style={{
            color: GOLD,
            textShadow: '0 0 28px rgba(216,184,120,0.45)'
          }} data-id="element-298">
              despertar de la programación
            </span>{' '}
            que te hizo creer que eso era vivir.
          </p>
        </motion.div>
      </div>
    </section>;
}